import { getProductColorHex, colorContributionRampBucketKey } from "./ColorContributionRamp";

/** Set to `true` to show COLOUR · SOH % ramps (header row + GP slide card) and compute SOH-by-colour data. */
export const SOH_COLOR_RAMP_ENABLED = false;

/** Period sell-through: sold in date range ÷ buy (inward) qty. */
export const getProductPeriodStPct = (product) => {
	const buy = Number(product?.troco_ls?.t_buy) || 0;
	const sold = Number(product?.troco_ls?.t_sale) || 0;
	if (buy <= 0) return 0;
	return Math.round((sold / buy) * 10000) / 100;
};

export const parseProductCogs = (product) => parseFloat(String(product?.COGS ?? "").replace(/,/g, "")) || 0;

/** Per-option GP (₹) — round each option, then sum in breakdowns (consistent with contribution chips). */
export const getProductGpValue = (product) => Math.round(parseFloat(product?.lcogs?.gp_value || 0));

export const sumGpValueForProducts = (products, { onlyPositive = false } = {}) =>
	(products || []).reduce((sum, product) => {
		const gp = getProductGpValue(product);
		if (onlyPositive && gp <= 0) return sum;
		return sum + gp;
	}, 0);

/** COGS buy value per option (COGS × T.INW) for colour-ramp contribution on the T.INW bar. */
export const getProductBuyCogsValue = (product) => {
	const cogs = parseProductCogs(product);
	const inw = Number(product?.troco_ls?.t_inw) || 0;
	return Math.round(cogs * inw);
};

export const formatRos = (value) => {
	const n = Number(value);
	if (value == null || value === "" || Number.isNaN(n) || n <= 0) return "—";
	return n.toFixed(2);
};

// Format number without decimals — Indian numbering system (e.g. 10,00,000)
export const formatNumber = (num) => {
	return Math.round(num).toLocaleString("en-IN");
};

/** Per-piece averages; MRP uses 3 dp so Avg × Pcs is closer to Value than integer rounding. */
export const formatAvgRate = (num, fractionDigits = 2) => {
	if (num == null || !Number.isFinite(Number(num))) return "0";
	return Number(num).toLocaleString("en-IN", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});
};

// Format number with 2 decimal places for GP
export const formatGPPercentage = (num) => {
	const value = Number(num);
	// If value is very small but not zero, show as 0.01 to avoid showing 0
	if (value > 0 && value < 0.01) {
		return 0.01;
	}
	return Math.round(value * 100) / 100;
};

// Format number with 2 decimal places for GP value
export const formatGPValue = (num) => {
	const value = Number(num);
	// If value is very small but not zero, round to 1 to avoid showing 0
	if (value > 0 && value < 1) {
		return 1;
	}
	return Math.round(value).toLocaleString("en-IN");
};

/** Ramp buckets by **colour label**, not resolved hex (avoids Off White + White merging to one #fff key). */
export function colorContributionMapKey(product) {
	return colorContributionRampBucketKey(product);
}

/**
 * Per-colour % of total for a product metric (only rows with positive values contribute).
 * Row `soldQty` holds the summed units; `hex` is set when the product exposes a hex (or `color` is hex).
 * Optional `getGpValue` — when passed, sum a secondary value per colour and attach `gpSharePct` (% of that total in the slice; sold ramp = GP, T.INW ramp = COGS buy value); bar width stays from the primary metric.
 */
export function aggregateColorMetricContributions(products, brandFilter, getMetricValue, getGpValue) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const colorMap = new Map();
	list.forEach((product) => {
		const colorName = String(product.color ?? "Unknown").trim() || "Unknown";
		const hex = getProductColorHex(product);
		const mapKey = colorContributionMapKey(product);
		const qty = Math.round(Number(getMetricValue(product)) || 0);
		const gpAdd = typeof getGpValue === "function" ? getProductGpValue(product) : 0;
		if (qty > 0) {
			if (colorMap.has(mapKey)) {
				const existing = colorMap.get(mapKey);
				existing.soldQty += qty;
				existing.count += 1;
				if (typeof getGpValue === "function") {
					existing.gpValue = (existing.gpValue || 0) + gpAdd;
				}
				if (!existing.hex && hex) existing.hex = hex;
			} else {
				colorMap.set(mapKey, {
					color: colorName,
					...(hex ? { hex } : {}),
					soldQty: qty,
					...(typeof getGpValue === "function" ? { gpValue: gpAdd } : {}),
					count: 1,
				});
			}
		}
	});

	const buckets = Array.from(colorMap.values());
	const totalSold = buckets.reduce((sum, item) => sum + item.soldQty, 0);
	const totalGp =
		typeof getGpValue === "function" ? buckets.reduce((sum, item) => sum + (item.gpValue || 0), 0) : 0;

	return buckets
		.map((item) => ({
			...item,
			percentage: totalSold > 0 ? Math.round((item.soldQty / totalSold) * 100 * 10) / 10 : 0,
			...(typeof getGpValue === "function" && totalGp > 0
				? {
						gpSharePct: Math.round(((item.gpValue || 0) / totalGp) * 100 * 10) / 10,
					}
				: {}),
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

/**
 * Per-colour SOH with WH vs EBO split (soldQty = WH + EBO for ramp width / %).
 */
export function aggregateColorSohContributions(products, brandFilter) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const colorMap = new Map();
	list.forEach((product) => {
		const colorName = String(product.color ?? "Unknown").trim() || "Unknown";
		const hex = getProductColorHex(product);
		const mapKey = colorContributionMapKey(product);
		const wh = Math.round(Number(product.total_wh_soh) || 0);
		const ebo = Math.round(Number(product.total_ebo_soh) || 0);
		const total = wh + ebo;
		if (total > 0) {
			if (colorMap.has(mapKey)) {
				const existing = colorMap.get(mapKey);
				existing.soldQty += total;
				existing.whSoh += wh;
				existing.eboSoh += ebo;
				existing.count += 1;
				if (!existing.hex && hex) existing.hex = hex;
			} else {
				colorMap.set(mapKey, {
					color: colorName,
					...(hex ? { hex } : {}),
					soldQty: total,
					whSoh: wh,
					eboSoh: ebo,
					count: 1,
				});
			}
		}
	});

	const grandTotal = Array.from(colorMap.values()).reduce((sum, item) => sum + item.soldQty, 0);

	return Array.from(colorMap.values())
		.map((item) => ({
			...item,
			percentage: grandTotal > 0 ? Math.round((item.soldQty / grandTotal) * 100 * 10) / 10 : 0,
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

/** Per-fashionability % of sold qty (period or overall) for straight divider bar. */
export function aggregateFashionabilityMetricContributions(products, brandFilter, getQty) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const fashionMap = new Map();
	list.forEach((product) => {
		const label = String(product.fashionability ?? "Unknown").trim() || "Unknown";
		const mapKey = label.replace(/\s+/g, " ").toLowerCase();
		const qty = Math.round(Number(getQty(product)) || 0);
		if (qty > 0) {
			if (fashionMap.has(mapKey)) {
				fashionMap.get(mapKey).soldQty += qty;
			} else {
				fashionMap.set(mapKey, { fashionability: label, soldQty: qty });
			}
		}
	});

	const buckets = Array.from(fashionMap.values());
	const totalSold = buckets.reduce((sum, item) => sum + item.soldQty, 0);

	return buckets
		.map((item) => ({
			...item,
			percentage: totalSold > 0 ? Math.round((item.soldQty / totalSold) * 100 * 10) / 10 : 0,
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

export const aggregateFashionabilitySoldContributions = (products, brandFilter) =>
	aggregateFashionabilityMetricContributions(products, brandFilter, (p) => p.troco_ls?.t_sale);

export const aggregateFashionabilityOverallContributions = (products, brandFilter) =>
	aggregateFashionabilityMetricContributions(products, brandFilter, (p) => p.troco_ls?.t_sale_total);

// Helper function for client-side sorting
export const sortProducts = (products, sortField, sortDirection) => {
	if (products.length === 0) {
		return products;
	}

	return [...products].sort((a, b) => {
		let aValue, bValue;

		// Map field names to actual data properties
		switch (sortField) {
			case "period_st":
				aValue = getProductPeriodStPct(a);
				bValue = getProductPeriodStPct(b);
				break;
			case "st":
				aValue = parseFloat(a.troco_ls?.st) || 0;
				bValue = parseFloat(b.troco_ls?.st) || 0;
				break;
			case "qty":
			case "sold_in_period":
				aValue = parseFloat(a.troco_ls?.t_sale) || 0;
				bValue = parseFloat(b.troco_ls?.t_sale) || 0;
				break;
			case "total_sold":
				aValue = parseFloat(a.troco_ls?.t_sale_total) || 0;
				bValue = parseFloat(b.troco_ls?.t_sale_total) || 0;
				break;
			case "inw_qty":
				// Inward / T.INW quantity
				aValue = parseFloat(String(a.troco_ls?.t_inw ?? "").replace(/,/g, "")) || 0;
				bValue = parseFloat(String(b.troco_ls?.t_inw ?? "").replace(/,/g, "")) || 0;
				break;
			case "cogs":
				aValue = parseProductCogs(a);
				bValue = parseProductCogs(b);
				break;
			case "gp_cont":
				// GP Contribution: gp_value
				aValue = parseFloat(a.lcogs?.gp_value) || 0;
				bValue = parseFloat(b.lcogs?.gp_value) || 0;
				break;
			case "period_ros":
				aValue = parseFloat(a.period_ros) || 0;
				bValue = parseFloat(b.period_ros) || 0;
				break;
			case "ros":
				aValue = parseFloat(a.rate_of_sale) || 0;
				bValue = parseFloat(b.rate_of_sale) || 0;
				break;
			case "doh":
				// Days on Hands - calculate average DOH
				const aDohValues = [a.ebo_doh || 0, a.lfs_doh || 0, a.mbo_doh || 0, a.ecom_doh || 0].filter((value) => value > 0);
				const bDohValues = [b.ebo_doh || 0, b.lfs_doh || 0, b.mbo_doh || 0, b.ecom_doh || 0].filter((value) => value > 0);

				aValue =
					aDohValues.length > 0 ? Math.round(aDohValues.reduce((sum, value) => sum + value, 0) / aDohValues.length) : 0;
				bValue =
					bDohValues.length > 0 ? Math.round(bDohValues.reduce((sum, value) => sum + value, 0) / bDohValues.length) : 0;
				break;
			case "aging": {
				const agingVal = (p) => {
					if (p.avg_aging != null && p.avg_aging !== "" && !Number.isNaN(Number(p.avg_aging))) {
						return Number(p.avg_aging);
					}
					const vals = [p.ebo_aging || 0, p.lfs_aging || 0, p.mbo_aging || 0, p.ecom_aging || 0].filter((v) => v > 0);
					return vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
				};
				aValue = agingVal(a);
				bValue = agingVal(b);
				break;
			}
			case "max_recv_aging":
			case "last_aging": {
				const lastAgingVal = (p) => {
					if (p.avg_max_aging != null && p.avg_max_aging !== "" && !Number.isNaN(Number(p.avg_max_aging))) {
						return Number(p.avg_max_aging);
					}
					const vals = [p.ebo_max_aging || 0, p.lfs_max_aging || 0, p.mbo_max_aging || 0, p.ecom_max_aging || 0].filter(
						(v) => v > 0
					);
					return vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
				};
				aValue = lastAgingVal(a);
				bValue = lastAgingVal(b);
				break;
			}
			case "wh_soh":
				aValue = Number(a.total_wh_soh) || 0;
				bValue = Number(b.total_wh_soh) || 0;
				break;
			case "ebo_soh":
				aValue = Number(a.total_ebo_soh) || 0;
				bValue = Number(b.total_ebo_soh) || 0;
				break;
			case "multi_mrp_cogs": {
				const mult = (p) => {
					const mrp = parseFloat(String(p.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
					const cogs = parseFloat(String(p.COGS ?? "").replace(/,/g, "")) || 0;
					if (!cogs) return 0;
					return mrp / cogs;
				};
				aValue = mult(a);
				bValue = mult(b);
				break;
			}
			default:
				return 0;
		}

		if (sortDirection === "asc") {
			return aValue - bValue;
		} else {
			return bValue - aValue;
		}
	});
};

/** CSV column aliases for the palette-type segment of a product key. */
const PRODUCT_KEY_PALETTE_COLUMNS = ["pallete type", "palette type", "pallete_type", "palette_type", "palette"];

const getCsvRowValue = (row, ...columnNames) => {
	for (const name of columnNames) {
		const val = row?.[name];
		if (val != null && String(val).trim()) return String(val).trim();
	}
	return "";
};

/** Build BRAND-DEPARTMENT-STYLE-COLOR-PALLETE_TYPE from one parsed CSV row. */
export const buildProductKeyFromCsvRow = (row) =>
	[
		getCsvRowValue(row, "brand"),
		getCsvRowValue(row, "department"),
		getCsvRowValue(row, "style"),
		getCsvRowValue(row, "color"),
		getCsvRowValue(row, ...PRODUCT_KEY_PALETTE_COLUMNS),
	].join("-");

/** Unique product keys from uploaded CSV rows (skips blank rows). */
export const buildProductKeysFromCsvRows = (rows) => {
	const seen = new Set();
	const keys = [];
	for (const row of rows || []) {
		const key = buildProductKeyFromCsvRow(row);
		if (!key.split("-").some((part) => part.length > 0)) continue;
		if (!seen.has(key)) {
			seen.add(key);
			keys.push(key);
		}
	}
	return keys;
};

// Mapping from filter field name to the selectedItems / filterOptions key
export const filterKeyMap = {
	key: "keys",
	brand: "brands",
	subBrand: "subBrands",
	department: "departments",
	season: "seasons",
	city: "cities",
	subCategory: "subCategories",
	palette: "palettes",
	fashionability: "fashionability",
	fitType: "fitTypes",
	fabricBlend: "fabricBlends",
	nod: "nods",
	channel: "channels",
	store: "stores",
	state: "states",
	region: "regions",
	tier: "tiers",
	aging: "aging",
	st_percentage: "st_percentage",
	// add more if needed
};

/** Selected values first (stable order within each group) so open dropdowns show current choices at the top. */
export const orderOptionsSelectedFirst = (optionsList, selected) => {
	if (!optionsList?.length || !selected || selected.size === 0) return optionsList;
	const atTop = [];
	const rest = [];
	for (const o of optionsList) {
		if (selected.has(o)) atTop.push(o);
		else rest.push(o);
	}
	return [...atTop, ...rest];
};

/** Sale-channel keys for period vs overall sold-pcs split in the summary header. */
export const PICTORIAL_CHANNEL_SOLD_DEF = [
	{ label: "EBO", periodKey: "ebo_sold_qty", overallKey: "ebo_sold_qty_total" },
	{ label: "LFS", periodKey: "lfs_sold_qty", overallKey: "lfs_sold_qty_total" },
	{ label: "MBO", periodKey: "mbo_sold_qty", overallKey: "mbo_sold_qty_total" },
	{ label: "D2C", periodKey: "d2c_sold_qty", overallKey: "d2c_sold_qty_total" },
	{ label: "MP", periodKey: "marketplace_sold_qty", overallKey: "marketplace_sold_qty_total" },
	{ label: "Other", periodKey: "other_sold_qty", overallKey: "other_sold_qty_total" },
];

export const channelSoldContributionPct = (part, total) => {
	if (!total || total <= 0) return "0.0";
	return (Math.round((part / total) * 1000) / 10).toFixed(1);
};
